import { config } from "@/src/config";
import { useAuthState, authSuccess } from "@/src/OS/Login/State";
import { Box, AHText } from "arkh";
import React, { useEffect } from "react";
import { getAbortControllerTimeout } from "./getAbortControllerTimeout";
import { Image } from "expo-image";
import { FormattedMessage } from "react-intl";
import { ActivityIndicator } from "react-native";
import { LoginWithIndividualAccountFooter } from "./LoginWithIndividualAccountFooter";

async function getTokenRes(res: Response) {
  const success = res.status === 200;
  return res.json().then((k) => {
    if (success) {
      return {
        success: true,
        authToken: k.access_token,
        idToken: k.id_token,
        refreshToken: k.refresh_token,
      };
    }
  });
}
export type PollingToken = {
  poll_token: string;
  poll_interval: number;
};

const POLL_INTERVAL = 2000;
export function Polling({
  pollToken,
  onError,
}: {
  pollToken: PollingToken;
  onError: () => void;
}) {
  const [, dispatch] = useAuthState();

  useEffect(() => {
    let isCancelled = false;
    let timeoutId: NodeJS.Timeout;
    let abortController: AbortController | null = null;

    async function poll() {
      try {
        abortController = getAbortControllerTimeout(10000);
        const res = await fetch(
          `${config.baseUrl}/shared_group_device/qr-login/poll`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            signal: abortController?.signal,
            body: JSON.stringify({
              poll_token: pollToken.poll_token,
            }),
          }
        );
        if (isCancelled) return;

        if (res.ok) {
          const data = await getTokenRes(res);
          if (isCancelled) return;

          if (data?.success) {
            dispatch(
              authSuccess(
                data.authToken,
                data.authToken, // we dont have id token for group device
                data.refreshToken,
                null,
                true
              )
            );
          } else {
            onError();
          }
        } else if (res.status !== 401) {
          onError();
        } else {
          timeoutId = setTimeout(poll, POLL_INTERVAL);
        }
      } catch {
        timeoutId = setTimeout(poll, POLL_INTERVAL);
      }
    }

    poll();
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
      abortController?.abort();
    };
  }, [dispatch, onError, pollToken.poll_token]);
  return <PollingScreen />;
}

function PollingScreen() {
  return (
    <Box
      style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
      color="surface-primary"
    >
      <Box
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          maxWidth: 700,
          maxHeight: 800,
          borderWidth: 1,
        }}
        space="large"
        cornerRadius="large"
        borderColor="border-primary"
        color="surface-primary"
      >
        <Image
          source={require("./group_device.png")}
          contentFit="contain"
          style={{ width: 100, height: 100 }}
        />
        <AHText type="title" textAlign="center" spaceVertical="medium">
          <FormattedMessage
            defaultMessage="Awaiting approval"
            description="PollingScreen awaiting approval title"
          />
        </AHText>
        <AHText
          type="subtitle"
          color="text-secondary"
          textAlign="center"
          spaceVertical="medium"
          spaceBottom="xlarge"
        >
          <FormattedMessage
            defaultMessage="Please wait while the admin approves your request."
            description="PollingScreen awaiting approval subtitle"
          />
        </AHText>

        <ContentWrapper>
          <ActivityIndicator />
        </ContentWrapper>
        <LoginWithIndividualAccountFooter />
      </Box>
    </Box>
  );
}

function ContentWrapper(props: { children: React.ReactNode }) {
  return (
    <Box
      style={{
        justifyContent: "center",
        alignItems: "center",
        minHeight: 100,
        minWidth: 460,
      }}
    >
      {props.children}
    </Box>
  );
}
